import { useAuth } from 'reactfire'
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  theme,
  Typography,
} from 'antd'
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PhoneAuthProvider, PhoneMultiFactorGenerator } from 'firebase/auth'
import { formGutter } from '../../lib/layout'
import { errorResponse } from '../../lib/errorResponse'
import { getPhoneDialCode } from '../../lib/phoneDialCode'
import { useRecaptchaVerifier } from '../../lib/useRecaptchaVerifier'

export function Verify2faAuthenticationModal({
  open,
  onCancel,
  resolver,
  successAction,
}) {
  const auth = useAuth()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()
  const [step, setStep] = useState(0)
  const [verificationId, setVerificationId] = useState(null)
  const [selectedResolverUid, setSelectedResolverUid] = useState(null)

  const { recaptchaVerifier, recaptchaLoading, setRecaptchaUsed } =
    useRecaptchaVerifier()
  const isSendingRef = useRef(false)

  useEffect(() => {
    // Auto-select phone number if only one is configured
    if (
      !recaptchaLoading &&
      recaptchaVerifier &&
      open &&
      resolver?.hints?.length === 1 &&
      lastSendTimestamp < Date.now() - 30000
    ) {
      const resolverUid = resolver.hints[0].uid
      setSelectedResolverUid(resolverUid)
      ;(async () => {
        const id = await handleSendCode(resolverUid)
        if (id) {
          setStep(1)
        }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver, open, recaptchaVerifier, recaptchaLoading])

  const [lastSendTimestamp, setLastSendTimestamp] = useState(0)

  const { token } = theme.useToken()
  const { t } = useTranslation()

  // Nothing can succeed once the MFA pending credential expired; restart login
  function onMfaSessionExpired() {
    message.error(t('frontend:common.errors.sessionExpired'))
    setStep(0)
    setVerificationId(null)
    setSelectedResolverUid(null)
    setLastSendTimestamp(0)
    onCancel()
  }

  async function onSendConfirmationCode(values) {
    const confirmationCode = values.confirmationCode
    setLoading(true)

    if (step === 0) {
      const id = await handleSendCode(selectedResolverUid)
      if (id) {
        setStep(1)
      }
      setLoading(false)
    } else if (step === 1) {
      if (!verificationId) {
        message.error(t('frontend:auth.requestCodeFirst'))
        setLoading(false)
        return
      }
      try {
        // Ask user for the SMS verification code. Then:
        const cred = PhoneAuthProvider.credential(
          verificationId,
          confirmationCode
        )
        const multiFactorAssertion =
          await PhoneMultiFactorGenerator.assertion(cred)
        // Complete sign-in.
        await resolver.resolveSignIn(multiFactorAssertion)
        setRecaptchaUsed(true)
        setStep(0)
        setLastSendTimestamp(0)
        await successAction()
      } catch (error) {
        switch (error.code) {
          case 'auth/invalid-verification-code':
            form.setFields([
              {
                name: ['confirmationCode'],
                value: '',
                errors: [t('frontend:auth.wrongCode')],
              },
            ])
            break
          case 'auth/code-expired':
            form.setFields([
              {
                name: ['confirmationCode'],
                value: '',
                errors: [t('frontend:auth.codeExpired')],
              },
            ])
            break
          case 'auth/invalid-multi-factor-session':
            onMfaSessionExpired()
            break
          case 'auth/missing-code':
            form.setFields([
              {
                name: ['confirmationCode'],
                value: '',
                errors: [t('frontend:auth.enterCode')],
              },
            ])
            break
          default:
            errorResponse(error)
            break
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSendCode = async (resolverUid, resend = false) => {
    if (isSendingRef.current) return null
    // The verifier is briefly null on first mount and during the
    // teardown/recreate after each send; verifyPhoneNumber rejects null with
    // auth/argument-error, so bail out and let the user retry.
    if (!recaptchaVerifier) {
      message.error(t('frontend:auth.codeSendError'))
      return null
    }
    isSendingRef.current = true
    setLastSendTimestamp(Date.now())

    const resolverObject = resolver.hints.find(
      (hint) => hint.uid === resolverUid
    )

    // Logic to resend the 2FA code
    try {
      const phoneInfoOptions = {
        multiFactorHint: resolverObject || resolver.hints[0],
        session: resolver.session,
      }

      const provider = new PhoneAuthProvider(auth)
      const verificationId = await provider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier
      )

      setVerificationId(verificationId)
      // RecaptchaVerifier tokens are single-use; recreate before any next send
      setRecaptchaUsed(true)
      if (resend) {
        message.success(t('frontend:auth.newCodeSent'))
      }
      return verificationId
    } catch (error) {
      if (error?.code === 'auth/invalid-multi-factor-session') {
        onMfaSessionExpired()
        return null
      }
      // For a blocked SMS destination country, let errorResponse surface its
      // specific region message instead of the generic send error, and tag
      // the event with the dial code from the (partially masked) hint so the
      // country is visible in Sentry (CL-1858 / CL-WEBAPP-N8).
      const dialCode = getPhoneDialCode(
        (resolverObject || resolver.hints[0])?.phoneNumber
      )
      errorResponse(
        error,
        error?.code === 'auth/operation-not-allowed'
          ? undefined
          : t('frontend:auth.codeSendError'),
        dialCode ? { phoneRegion: dialCode } : undefined
      )
      return null
    } finally {
      isSendingRef.current = false
    }
  }

  return (
    <Modal
      title={t('frontend:auth.twoFactorTitle')}
      open={open}
      maskClosable={false}
      loading={recaptchaLoading}
      onCancel={onCancel}
      afterOpenChange={(open) => open && inputRef.current?.focus()}
      afterClose={() => form.resetFields()}
      footer={null}
    >
      <br />
      <Form
        colon={false}
        layout="vertical"
        requiredMark={false}
        form={form}
        onValuesChange={(changedValues) => {
          if (changedValues.phoneNumber) {
            setSelectedResolverUid(changedValues.phoneNumber)
          }
        }}
        preserve={true}
      >
        <Row gutter={formGutter}>
          {step === 0 && (
            <>
              <Col span={24}>
                <Steps current={step} size="small">
                  <Steps.Step title={t('frontend:auth.selectPhoneNumber')} />
                  <Steps.Step title={t('frontend:auth.confirmCode')} />
                </Steps>
              </Col>
              <Col span={24}>
                <Form.Item
                  name={['phoneNumber']}
                  style={{ marginTop: token.marginMD }}
                  rules={[
                    {
                      required: true,
                      message: t('frontend:auth.selectPhoneNumberRequired'),
                    },
                  ]}
                >
                  <Select placeholder={t('frontend:auth.phoneNumber')}>
                    {resolver?.hints?.map((hint, index) => (
                      <Select.Option key={index} value={hint.uid}>
                        {hint.phoneNumber}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
          {step === 1 && (
            <>
              <Col span={24}>
                <Trans
                  i18nKey="frontend:auth.codeSentTo"
                  values={{
                    phoneNumber:
                      resolver?.hints?.find(
                        (hint) => hint.uid === selectedResolverUid
                      )?.phoneNumber ?? '',
                  }}
                  components={{ italic: <Typography.Text italic /> }}
                />
              </Col>
              <Col span={24}>
                <ConfigProvider
                  theme={{
                    components: {
                      Input: {
                        fontSize: token.fontSizeLG,
                        controlHeightLG: 50,
                        borderRadiusLG: token.borderRadius,
                      },
                    },
                  }}
                >
                  <Form.Item
                    label={t('frontend:auth.confirmationCode')}
                    name={['confirmationCode']}
                    style={{ textAlign: 'center', marginTop: token.marginMD }}
                    rules={[
                      {
                        required: true,
                        message: t('frontend:auth.enterCodePlease'),
                      },
                      {
                        len: 6,
                        message: t('frontend:auth.codeLength'),
                      },
                    ]}
                  >
                    <Input.OTP
                      autoFocus
                      size="large"
                      style={{ width: '100%' }}
                      formatter={(str) => str.toUpperCase()}
                      ref={inputRef}
                      onChange={async (val) => {
                        await onSendConfirmationCode({
                          confirmationCode: val,
                        })
                      }}
                    />
                  </Form.Item>
                </ConfigProvider>
              </Col>
              <Col span={24}>
                <Typography.Text type="secondary" style={{ fontSize: 'small' }}>
                  {t('frontend:auth.noCodeReceived')}{' '}
                  <ResendButton
                    resendCode={() => handleSendCode(selectedResolverUid, true)}
                    lastSendTimestamp={lastSendTimestamp}
                  />
                </Typography.Text>
              </Col>
            </>
          )}
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="primary"
                onClick={async () => {
                  await onSendConfirmationCode({
                    confirmationCode: form.getFieldValue('confirmationCode'),
                  })
                }}
                loading={loading || recaptchaLoading}
              >
                {step === 0
                  ? t('frontend:auth.requestCode')
                  : t('frontend:common.confirm')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

function ResendButton({ resendCode, lastSendTimestamp }) {
  const { t } = useTranslation()
  function getCountdownSeconds(timestamp) {
    return Math.max(0, Math.floor((timestamp + 30000 - Date.now()) / 1000))
  }

  const [countdown, setCountdown] = useState(
    getCountdownSeconds(lastSendTimestamp)
  )

  function send() {
    setCountdown(getCountdownSeconds(Date.now()))
    resendCode()
  }

  useEffect(() => {
    const timerId = setInterval(() => {
      setCountdown(getCountdownSeconds(lastSendTimestamp))
    }, 500)
    return () => clearInterval(timerId)
  }, [lastSendTimestamp])

  const canResend = getCountdownSeconds(lastSendTimestamp) === 0

  return (
    <Button type="link" onClick={send} disabled={!canResend}>
      {canResend
        ? t('frontend:auth.resend')
        : t('frontend:auth.resendIn', { seconds: countdown })}
    </Button>
  )
}
